import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
} from '@mui/material';

export interface DataTableColumn<T = unknown> {
  id: string;
  label: string;
  align?: 'left' | 'right' | 'center';
  minWidth?: number;
  /** Render cell content. Receives row and row index. */
  render: (row: T, index: number) => React.ReactNode;
}

export interface DataTablePaginationProps {
  page: number; // 0-based
  rowsPerPage: number;
  total: number;
  onPageChange: (newPage: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
  rowsPerPageOptions?: number[];
}

export interface DataTableProps<T = unknown> {
  columns: DataTableColumn<T>[];
  rows: T[];
  /** Key to use for row (e.g. '_id') or function (row) => key */
  rowKey: keyof T | ((row: T) => string);
  pagination?: DataTablePaginationProps;
  emptyMessage?: string;
  loading?: boolean;
  /** Optional: custom sx for a row (e.g. highlight today) */
  getRowSx?: (row: T, index: number) => Record<string, unknown> | undefined;
  size?: 'small' | 'medium';
  stickyHeader?: boolean;
}

function getRowKey<T>(row: T, rowKey: keyof T | ((row: T) => string)): string {
  return typeof rowKey === 'function' ? rowKey(row) : String(row[rowKey] ?? '');
}

function DataTableInner<T = unknown>({
  columns,
  rows,
  rowKey,
  pagination,
  emptyMessage = 'No data.',
  loading = false,
  getRowSx,
  size = 'small',
  stickyHeader = true,
}: DataTableProps<T>) {
  const total = pagination?.total ?? rows.length;
  const isEmpty = rows.length === 0 && !loading;

  return (
    <>
      <TableContainer>
        <Table size={size} stickyHeader={stickyHeader}>
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell
                  key={col.id}
                  align={col.align}
                  sx={{ fontWeight: 600, minWidth: col.minWidth }}
                >
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={columns.length}>
                  <Typography variant="body2" color="text.secondary">
                    Loading…
                  </Typography>
                </TableCell>
              </TableRow>
            ) : isEmpty ? (
              <TableRow>
                <TableCell colSpan={columns.length}>
                  <Typography variant="body2" color="text.secondary">
                    {emptyMessage}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row, index) => (
                <TableRow
                  key={getRowKey(row, rowKey)}
                  sx={getRowSx?.(row, index)}
                >
                  {columns.map((col) => (
                    <TableCell key={col.id} align={col.align} sx={{ minWidth: col.minWidth }}>
                      {col.render(row, index)}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {pagination && total > 0 && (
        <TablePagination
          component="div"
          count={pagination.total}
          page={pagination.page}
          onPageChange={(_, newPage) => pagination.onPageChange(newPage)}
          rowsPerPage={pagination.rowsPerPage}
          onRowsPerPageChange={(e) => {
            const v = parseInt(e.target.value, 10);
            if (!Number.isNaN(v)) pagination.onRowsPerPageChange(v);
          }}
          rowsPerPageOptions={pagination.rowsPerPageOptions ?? [5, 10, 25, 50]}
          labelRowsPerPage="Rows:"
          showFirstButton
          showLastButton
        />
      )}
    </>
  );
}

const DataTable = DataTableInner as <T = unknown>(props: DataTableProps<T>) => React.ReactElement;

export default DataTable;
